/**
 * AUTO-GENERATED — DO NOT EDIT
 *
 * Runtime error helpers: wrapError, errorMessage, transportErrorMessage.
 * Imports i18n for translation — consumers that only need types should
 * import from endpoint-error-types.ts instead.
 * Run `npm run monsterrat:generate:contracts` to regenerate.
 */

import { isAxiosError } from 'axios';
import NetInfo from '@react-native-community/netinfo';
import { createLogger } from '../../utils/logger';
import { ApiErrorResponseSchema } from './http-contracts';
import type { ApiErrorResponse } from './http-contracts';
import type { TransportError, DomainErrorBase, StoreErrorInfo } from './endpoint-error-types';

// Re-export all types so existing imports from 'endpoint-errors' keep working
export * from './endpoint-error-types';

const log = createLogger('apiError');

// ══════════════════════════════════════════════════════════════
// Error Translation
// ══════════════════════════════════════════════════════════════

type ErrorI18nKey = `errors.api.${string}`;

const ERROR_MESSAGES: Record<string, { key: ErrorI18nKey; fallback: string }> = {
  VALIDATION_FAILED: { key: 'errors.api.VALIDATION_FAILED' as ErrorI18nKey, fallback: 'Please check your input and try again.' },
  NOT_FOUND: { key: 'errors.api.NOT_FOUND' as ErrorI18nKey, fallback: 'The requested resource was not found.' },
  USER_NOT_FOUND: { key: 'errors.api.USER_NOT_FOUND' as ErrorI18nKey, fallback: 'User not found.' },
  ALREADY_EXISTS: { key: 'errors.api.ALREADY_EXISTS' as ErrorI18nKey, fallback: 'This resource already exists.' },
  CONFLICT: { key: 'errors.api.CONFLICT' as ErrorI18nKey, fallback: 'This conflicts with an existing resource.' },
  RATE_LIMITED: { key: 'errors.api.RATE_LIMITED' as ErrorI18nKey, fallback: 'Too many requests. Please wait a moment.' },
  QUOTA_EXCEEDED: { key: 'errors.api.QUOTA_EXCEEDED' as ErrorI18nKey, fallback: 'Usage limit reached.' },
  UNAUTHORIZED: { key: 'errors.api.UNAUTHORIZED' as ErrorI18nKey, fallback: 'Authentication required. Please sign in.' },
  TOKEN_EXPIRED: { key: 'errors.api.TOKEN_EXPIRED' as ErrorI18nKey, fallback: 'Your session has expired. Please sign in again.' },
  FORBIDDEN: { key: 'errors.api.FORBIDDEN' as ErrorI18nKey, fallback: 'Access denied.' },
  AI_CONSENT_REQUIRED: { key: 'errors.api.AI_CONSENT_REQUIRED' as ErrorI18nKey, fallback: 'Please accept AI data sharing to use this feature.' },
  ACCOUNT_DELETED: { key: 'errors.api.ACCOUNT_DELETED' as ErrorI18nKey, fallback: 'This account has been deleted.' },
  INTERNAL_ERROR: { key: 'errors.api.INTERNAL_ERROR' as ErrorI18nKey, fallback: 'Something went wrong.' },
  SERVICE_UNAVAILABLE: { key: 'errors.api.SERVICE_UNAVAILABLE' as ErrorI18nKey, fallback: 'Service temporarily unavailable. Please try later.' },
  AI_SERVICE_ERROR: { key: 'errors.api.AI_SERVICE_ERROR' as ErrorI18nKey, fallback: 'AI service error. Please try again.' },
  AI_PROVIDER_ERROR: { key: 'errors.api.AI_PROVIDER_ERROR' as ErrorI18nKey, fallback: 'AI service error. Please try again.' },
  AI_EMPTY_RESPONSE: { key: 'errors.api.AI_EMPTY_RESPONSE' as ErrorI18nKey, fallback: 'No response received. Please try again.' },
  AI_STREAM_ERROR: { key: 'errors.api.AI_STREAM_ERROR' as ErrorI18nKey, fallback: 'Response interrupted. Please try again.' },
  DATABASE_ERROR: { key: 'errors.api.DATABASE_ERROR' as ErrorI18nKey, fallback: 'Something went wrong.' },
  ROLEPLAY_SLOTS_FULL: { key: 'errors.api.ROLEPLAY_SLOTS_FULL' as ErrorI18nKey, fallback: 'You already have {{cap}} role plays. Delete one to create a new scenario.' },
  ROLEPLAY_SLOT_NOT_FOUND: { key: 'errors.api.ROLEPLAY_SLOT_NOT_FOUND' as ErrorI18nKey, fallback: 'This role play no longer exists.' },
  ROLEPLAY_SETUP_INVALID: { key: 'errors.api.ROLEPLAY_SETUP_INVALID' as ErrorI18nKey, fallback: 'Something went wrong with the setup. Try again.' },
  ROLEPLAY_TEMPLATE_NOT_FOUND: { key: 'errors.api.ROLEPLAY_TEMPLATE_NOT_FOUND' as ErrorI18nKey, fallback: 'This template is no longer available.' },
  ROLEPLAY_COLLECTION_NOT_FOUND: { key: 'errors.api.ROLEPLAY_COLLECTION_NOT_FOUND' as ErrorI18nKey, fallback: 'This collection is no longer available.' },
  VOCAB_ITEM_NOT_FOUND: { key: 'errors.api.VOCAB_ITEM_NOT_FOUND' as ErrorI18nKey, fallback: 'This vocabulary item is no longer available.' },
  VOCAB_SET_NOT_FOUND: { key: 'errors.api.VOCAB_SET_NOT_FOUND' as ErrorI18nKey, fallback: 'This word set is no longer available.' },
  EXPRESSION_ITEM_NOT_FOUND: { key: 'errors.api.EXPRESSION_ITEM_NOT_FOUND' as ErrorI18nKey, fallback: 'This expression is no longer available.' },
  GRAMMAR_POINT_NOT_FOUND: { key: 'errors.api.GRAMMAR_POINT_NOT_FOUND' as ErrorI18nKey, fallback: 'That focus area is no longer available.' },
  SESSION_BUSY: { key: 'errors.api.SESSION_BUSY' as ErrorI18nKey, fallback: 'Another message is already being processed. Please wait.' },
  STREAM_ERROR: { key: 'errors.api.STREAM_ERROR' as ErrorI18nKey, fallback: 'Stream failed. Please try again.' },
};

const TRANSPORT_MESSAGES: Record<TransportError['code'], { key: ErrorI18nKey; fallback: string }> = {
  OFFLINE: { key: 'errors.api.OFFLINE' as ErrorI18nKey, fallback: "You're offline. Changes will sync when connected." },
  TIMEOUT: { key: 'errors.api.TIMEOUT' as ErrorI18nKey, fallback: 'Request timed out. Please try again.' },
  SERVER_UNREACHABLE: { key: 'errors.api.SERVER_UNREACHABLE' as ErrorI18nKey, fallback: "Can't reach the server. Please try later." },
  SERVER_ERROR: { key: 'errors.api.INTERNAL_ERROR' as ErrorI18nKey, fallback: 'Something went wrong.' },
  UNKNOWN: { key: 'errors.api.UNKNOWN' as ErrorI18nKey, fallback: 'Something went wrong.' },
};

// eslint-disable-next-line @typescript-eslint/no-var-requires
let _i18n: typeof import('../../i18n').default | null = null;
function getI18n() { return _i18n ?? (_i18n = require('../../i18n').default); }

function translateErrorCode(key: ErrorI18nKey, fallback: string, params?: Record<string, unknown>): string {
  const translated = getI18n().t(key, { defaultValue: '', ...params });
  if (translated) return translated;
  log.warn('Missing i18n error key', { key, fallback });
  if (!params) return fallback;
  return fallback.replace(/\{\{(\w+)\}\}/g, (_, k) => {
    const v = params[k];
    return v !== undefined ? String(v) : `{{${k}}}`;
  });
}

function isApiErrorResponse(data: unknown): data is ApiErrorResponse {
  return ApiErrorResponseSchema.safeParse(data).success;
}

async function classifyTransportError(error: unknown): Promise<TransportError> {
  if (isAxiosError(error)) {
    if (error.response) {
      return { code: 'SERVER_ERROR' as const, status: error.response.status, retryable: true as const, referenceId: undefined };
    }
    if (error.code === 'ECONNABORTED' || error.message?.toLowerCase().includes('timeout')) {
      return { code: 'TIMEOUT' as const, retryable: true as const };
    }
    try {
      const netState = await Promise.race([
        NetInfo.fetch(),
        new Promise<{ isConnected: null }>((resolve) => setTimeout(() => resolve({ isConnected: null }), 1000)),
      ]);
      if (netState.isConnected === false) return { code: 'OFFLINE' as const, retryable: true as const };
    } catch {
      log.debug('NetInfo fetch failed, assuming server unreachable');
    }
    return { code: 'SERVER_UNREACHABLE' as const, retryable: true as const };
  }
  return { code: 'UNKNOWN' as const, retryable: false as const };
}

// ══════════════════════════════════════════════════════════════
// wrapError — converts caught errors into Result error branches
// ══════════════════════════════════════════════════════════════

export async function wrapError<D>(
  error: unknown,
  endpoint?: string,
): Promise<
  | { ok: false; kind: 'domain'; error: D & DomainErrorBase }
  | { ok: false; kind: 'transport'; error: TransportError }
> {
  if (isAxiosError(error) && error.response?.data && isApiErrorResponse(error.response.data)) {
    const data = error.response.data;
    const apiCode = data.error.code;
    const detailsParams = data.details && typeof data.details === 'object'
      ? (data.details as Record<string, unknown>) : undefined;
    const entry = ERROR_MESSAGES[apiCode];
    const message = entry
      ? translateErrorCode(entry.key, entry.fallback, detailsParams)
      : translateErrorCode('errors.api.UNKNOWN' as ErrorI18nKey, 'Something went wrong.');
    if (data.error.category === 'UNEXPECTED') {
      log.error(
        `${endpoint ?? 'API'}: ${apiCode}`, error,
        { errorCode: apiCode, errorCategory: data.error.category, endpoint, referenceId: data.referenceId },
      );
    }
    return {
      ok: false,
      kind: 'domain',
      error: {
        code: apiCode, category: data.error.category, message,
        fieldErrors: (data.details as { fields?: Record<string, string[]> })?.fields,
        retryAfter: (data.details as { retryAfter?: number })?.retryAfter,
        referenceId: data.referenceId,
      } as unknown as D & DomainErrorBase,
    };
  }
  const transport = await classifyTransportError(error);
  if (transport.code === 'SERVER_ERROR' || transport.code === 'UNKNOWN') {
    log.error(
      `${endpoint ?? 'API'}: transport/${transport.code}`, error,
      { transportCode: transport.code, endpoint },
    );
  }
  return { ok: false, kind: 'transport', error: transport };
}

/** Get a translated user-facing message for a transport error */
export function transportErrorMessage(error: TransportError): string {
  const entry = TRANSPORT_MESSAGES[error.code];
  return translateErrorCode(entry.key, entry.fallback);
}

/** Get user-facing message from any StoreErrorInfo */
export function errorMessage(info: StoreErrorInfo): string {
  if (info.kind === 'domain') return info.error.message;
  return transportErrorMessage(info.error);
}

